import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { AdminListPrescreensResponse } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import type { TableColumn } from '@workspace/ui/components/data-table'
import { CellText, DataTable } from '@workspace/ui/components/data-table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog'
import { Pagination } from '@workspace/ui/components/pagination'
import { SearchInput } from '@workspace/ui/components/search-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select'
import { CircleX, Clock, Loader2, Plus } from 'lucide-react'
import { useState } from 'react'
import { ScoreBadge } from '@/features/pitch/components/score-badge'
import {
  useAdminEligibleStartupsQuery,
  useAdminPrescreensQuery,
  useAdminTriggerPrescreenMutation,
} from '@/hooks/api'
import { CellLink } from '@/utils/data-table'
import { formatFullDate } from '@/utils/format'

type PrescreenListItem = AdminListPrescreensResponse['data']['items'][number]

type SearchParams = {
  page?: number
  search?: string
}

export const Route = createFileRoute('/_protected/admin/prescreens/')({
  component: AdminPrescreensPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    search: (search.search as string) || undefined,
  }),
})

const LIMIT = 20

const statusIcon: Record<string, React.ReactNode> = {
  FAILED: <CircleX className="size-3.5 text-destructive" />,
  PENDING: <Clock className="size-3.5 text-muted-foreground" />,
}

const columns: TableColumn<PrescreenListItem>[] = [
  {
    header: 'ID',
    width: '100px',
    render: (prescreen) =>
      prescreen.status === 'COMPLETED' ? (
        <CellLink
          to="/admin/prescreens/$id"
          params={{ id: prescreen.id }}
          className="font-mono text-xs"
        >
          {prescreen.id.slice(0, 8)}
        </CellLink>
      ) : (
        <CellText className="font-mono text-xs">{prescreen.id.slice(0, 8)}</CellText>
      ),
  },
  {
    header: 'Startup',
    render: (prescreen) => (
      <CellLink
        to="/admin/startups/$id"
        params={{ id: prescreen.startupId }}
        className="font-medium"
      >
        {prescreen.startupName ?? 'Unknown'}
      </CellLink>
    ),
  },
  {
    header: 'Investor',
    render: (prescreen) =>
      prescreen.investorId ? (
        <CellLink to="/admin/investors/$id" params={{ id: prescreen.investorId }}>
          {prescreen.investorName ?? 'Unknown'}
        </CellLink>
      ) : (
        <CellText className="text-muted-foreground">-</CellText>
      ),
  },
  {
    header: 'Score',
    width: '80px',
    render: (prescreen) => <ScoreBadge score={prescreen.score} size="sm" />,
  },
  {
    header: 'Status',
    width: '130px',
    render: (prescreen) =>
      prescreen.status === 'COMPLETED' ? (
        <Badge variant="success" size="sm">
          {prescreen.status}
        </Badge>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-sm">
          {statusIcon[prescreen.status] ?? <Clock className="size-3.5 text-muted-foreground" />}
          <span className="text-muted-foreground text-xs">{prescreen.status}</span>
        </span>
      ),
  },
  {
    header: 'Date',
    width: '130px',
    render: (prescreen) => <CellText>{formatFullDate(prescreen.createdAt)}</CellText>,
  },
]

function NewPrescreenDialog() {
  const [open, setOpen] = useState(false)
  const [selectedStartupId, setSelectedStartupId] = useState('')

  const startupsQuery = useAdminEligibleStartupsQuery(undefined, { enabled: open })

  const selectedStartup = startupsQuery.data?.data.find((s) => s.id === selectedStartupId)

  const triggerMutation = useAdminTriggerPrescreenMutation({
    onSuccess: () => {
      setOpen(false)
      setSelectedStartupId('')
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) {
          setSelectedStartupId('')
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="mr-1.5 size-4" />
          New Prescreen
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Prescreen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Select a startup to prescreen using its latest pitch deck.
            </p>
            <Select
              value={selectedStartupId}
              onValueChange={setSelectedStartupId}
              disabled={startupsQuery.isPending}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={startupsQuery.isPending ? 'Loading startups...' : 'Select a startup'}
                />
              </SelectTrigger>
              <SelectContent>
                {startupsQuery.data?.data.map((startup) => (
                  <SelectItem key={startup.id} value={startup.id}>
                    {startup.name ?? 'Unnamed'}
                  </SelectItem>
                ))}
                {startupsQuery.data?.data.length === 0 && (
                  <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                    No eligible startups found.
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          <Button
            className="w-full"
            disabled={!selectedStartup || triggerMutation.isPending}
            onClick={() =>
              selectedStartup &&
              triggerMutation.mutate({
                body: { startupId: selectedStartup.id, documentId: selectedStartup.pitchDeckId },
              })
            }
          >
            {triggerMutation.isPending ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : null}
            Run Prescreen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function AdminPrescreensPage() {
  const { page = 1, search } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const [searchInput, setSearchInput] = useState(search ?? '')
  const prescreensQuery = useAdminPrescreensQuery({
    query: { page, limit: LIMIT, search: search || undefined },
  })

  const prescreens = prescreensQuery.data?.data?.items ?? []
  const total = prescreensQuery.data?.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))

  return (
    <div className="w-full space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SearchInput
            placeholder="Search by startup name..."
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() => navigate({ search: { page: 1, search: searchInput || undefined } })}
          />
        </div>
        <NewPrescreenDialog />
      </div>
      <DataTable
        columns={columns}
        data={prescreens}
        isLoading={prescreensQuery.isLoading}
        emptyMessage="No pre-screens found."
        keyExtractor={(p) => p.id}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={(p) => navigate({ search: { page: p, search } })}
      />
    </div>
  )
}
